// src/App.jsx
import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import HomePage from "./pages/HomePage";
import TourPage from "./pages/TourPage";
import ContactPage from "./pages/ContactPage";
import { ChevronDown, MapPin, Image, Building2, X } from "lucide-react";
import FloorPlanPage from "./pages/FloorPlan";
import { AudioProvider } from "./context/AudioContext";
import AssetPreloader from "./components/common/AssetPreloader";

/**
 * App component serves as the main entry point for the React application.
 * It manages the current page view using React Router.
 */
function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [assetsLoaded, setAssetsLoaded] = useState(false);

  const handleAssetsLoaded = () => {
    setAssetsLoaded(true);
    console.log('All assets have been preloaded and cached!');
  };

  return (
    <AudioProvider>
      <Router>
        <AssetPreloader onComplete={handleAssetsLoaded}>
          <div className="App flex flex-col min-h-screen bg-gray-900">
            {/* Navigation Header */}
            <header className="fixed top-0 left-0 w-full z-50 bg-transparent border-b border-white/20 backdrop-blur-md">
              <div className="container mx-auto flex justify-between items-center px-4 py-4">
                {/* Left spacer (or menu button in future) */}
                <div className="w-1/3 flex justify-start">
                  {/* Optional: put a back/home/menu icon here */}
                </div>

                {/* Center logo */}
                <div className="w-1/3 flex justify-center">
                  <Link to="/">
                    <img
                      src="assets/images/bnw-logo.png"
                      alt="Apartment Tour Logo"
                      className="h-10 object-contain"
                      style={{ maxWidth: 160 }}
                    />
                  </Link>
                </div>

                {/* Right spacer (optional future buttons) */}
                <div className="w-1/3 flex justify-end">
                  {/* <button onClick={() => setMenuOpen(true)}>
                    <img src="assets/icons/menu.png" alt="Menu" className="h-10 w-10" />
                  </button> */}
                </div>
              </div>
            </header>

            {/* Main Content Area: Renders the active page based on the route */}
            <main className="flex-grow pt-20">
              {/* Use Routes to define your application's routes */}
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/tour" element={<TourPage />} />
                <Route path="/floorplan" element={<FloorPlanPage />} />
                <Route path="/contact" element={<ContactPage />} />
                {/* Define a fallback route for unmatched paths */}
                <Route path="*" element={<HomePage />} />
              </Routes>
            </main>

            {menuOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
                {/* Dimmed Background Image */}
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: "url('/assets/images/home-bg.png')" }}
                />
                <div className="absolute inset-0 bg-black opacity-60" />{" "}
                {/* Dim layer */}
                {/* Back Button */}
                <button
                  onClick={() => setMenuOpen(false)}
                  className="absolute top-6 right-6 text-white z-10"
                >
                  <X size={32} />
                </button>
                {/* Centered Card */}
                <div className="z-10 bg-white/30 backdrop-blur-md rounded-2xl px-16 py-12 text-white text-center space-y-8">
                  <div className="flex justify-around items-center gap-16 border-b border-white/20 pb-8">
                    {/* 1 BHK Section */}
                    <div className="flex flex-col items-center space-y-3">
                      <h2 className="text-8xl font-bold">1 BHK</h2>
                      {/* <button className="bg-white text-black w-full px-6 py-2 rounded-full">Floor Plan</button> */}
                      <Link
                        to="/floorplan"
                        onClick={() => setMenuOpen(false)}
                        className="bg-white text-black w-full px-6 py-2 rounded-full"
                      >
                        Floor Plan
                      </Link>
                      {/* Use Link for navigation within the menu */}
                      <Link
                        to="/tour"
                        onClick={() => setMenuOpen(false)}
                        className="bg-[#6B6663] w-full px-6 py-2 rounded-full"
                      >
                        360 Tour
                      </Link>
                    </div>

                    {/* Vertical Divider */}
                    <div className="w-px h-36 bg-white opacity-30" />

                    <div className="flex flex-col items-center space-y-3">
                      <h2 className="text-8xl font-bold">2 BHK</h2>
                      <button
                        disabled
                        className="bg-white text-black w-full px-6 py-2 rounded-full opacity-50 cursor-not-allowed"
                      >
                        Floor Plan
                      </button>
                      <button
                        disabled
                        className="bg-[#6B6663] w-full px-6 py-2 rounded-full opacity-50 cursor-not-allowed"
                      >
                        360 Tour
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap justify-between gap-4 text-sm">
                    {/* Dropdown Language Button */}
                    <button className="bg-[#252525C2] w-full max-w-[140px] px-4 py-2 rounded-full flex items-center justify-between">
                      <span></span>
                      <span>English</span>
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </button>

                    {/* Amenities */}
                    <button className="bg-[#252525C2] w-full max-w-[140px] px-4 py-2 rounded-full flex items-center justify-between gap-2">
                      <img
                        src="/assets/icons/amenities.png"
                        alt="Amenities"
                        className="w-8 h-8"
                      />
                      <span>Amenities</span>
                      <span></span>
                    </button>

                    {/* Gallery */}
                    <button className="bg-[#252525C2] w-full max-w-[140px] px-4 py-2 rounded-full flex items-center justify-between gap-2">
                      <img
                        src="/assets/icons/gallery.png"
                        alt="Gallery"
                        className="w-8 h-8"
                      />
                      <span>Gallery</span>
                      <span></span>
                    </button>

                    {/* Location */}
                    <button className="bg-[#252525C2] w-full max-w-[140px] px-4 py-2 rounded-full flex items-center justify-between gap-2">
                      <img
                        src="/assets/icons/location.png"
                        alt="Location"
                        className="w-8 h-8"
                      />
                      <span>Location</span>
                      <span></span>
                    </button>
                  </div>

                  {/* Use Link for "Enquire Now" to navigate to the Contact Page */}
                  <Link
                    to="/contact"
                    onClick={() => setMenuOpen(false)}
                    className="bg-[#402020] w-full hover:bg-red-700 transition px-10 py-3 rounded-full text-white inline-block"
                  >
                    Enquire Now
                  </Link>
                </div>
              </div>
            )}
          </div>
        </AssetPreloader>
      </Router>
    </AudioProvider>
  );
}

export default App;